import React, { useState, useEffect, useCallback, useMemo } from "react";
import HtmlParser, { domToReact, attributesToProps } from "html-react-parser";
import { connect } from "react-redux";
import Ruler from "@scena/react-ruler";
import { useLocation, useNavigate } from "react-router-dom";
import _, { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import LottieAnimation from "us.common/components/Lottie";
import { LoadingPages } from "us.common/components/animations";
import {
  $Drawer,
  $Button,
  $Divider,
  $Tooltip,
  $Empty,
  $Message,
} from "us.common/components";
import {
  FileAddOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  AuditOutlined,
  FileDoneOutlined,
  BoxIcons,
  IconTypes,
} from "us.icons";
import {
  SectionRules,
  TemplateRules,
  USCSection,
  PageWrapper,
  PagesAndSections,
  PageMargins,
  NavigatePrompt,
  RichTextEditor,
  SMSEditor,
  RepeatingAdapterChanger,
  VersionHistory,
} from "./Components";
import * as Actions from "actions";
import {
  createGuides,
  getUSCSectionStyleClass,
  generateTemplateHtml,
  addNewSectionToTemplate,
  setToolBarOffset,
  getSectionData,
  updateSectionRuleProperties,
} from "./Functions";
import "./home.scss";
import { LEFT_PANELS, EditorAction, DEFAULT_MENU_DATA } from "./Constants";
import { URLParams } from "components/EditorHome/Constants";
import { DEFAULT_DRAWER_STATE, DrawerType } from "constants/TemplateEditor";
import {
  getPageProperties,
  getSectionByName,
  getSectionRuleByRule,
  updateTemplateSectionRule,
} from "functions";
import { RuleEditor, Parameters, Preview } from "components";
import { DEFAULT_SECTION_DATA } from "constants/TemplateEditor";

const rulerProps = {
  direction: "end",
  negativeRuler: false,
  backgroundColor: "rgba(0,0,0,0)",
  textColor: "#8A8D91",
  lineColor: "#bbbcbe",
  unit: 1,
  zoom: 37.7952,
  segment: 10,
};

function USCTemplate({ children }) {
  return <>{children}</>;
}

/**
 * @description - USC Template editor component
 * @link Design Document -
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 10/06/2022
 */
const TemplateEditor = (props) => {
  const {
    section,
    template,
    drawerInfo,
    selectedTemplateId,
    selectSection,
    updateTemplate,
    manageDrawer,
    getTemplateDetails,
    getTemplates,
    initTemplate,
  } = props;

  const { t } = useTranslation();

  const { search } = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(search);

  const documentType = urlParams.get(URLParams.DOCUMENT_TYPE);
  const pageProperties = getPageProperties(documentType);
  const templateId = urlParams.get("id");

  const [zoomLevel, setZoomLevel] = useState(100);
  const [editorControls, setEditorControls] = useState(DEFAULT_MENU_DATA);
  const templateContent = template.data?.templateContent ?? {};

  const content = useMemo(() => {
    if (!template.loading && template.data) {
      const { pages } = templateContent;
      if (pages) {
        const content = generateTemplateHtml(templateContent);
        return content;
      }
    } else {
      return `<></>`;
    }
  }, [template]);

  const handleDrop = useCallback(
    (index, item, dropPosition, actionType) => {
      if (template.data && templateContent) {
        const { pages } = templateContent;

        const updatedPages = addNewSectionToTemplate(
          item,
          pages,
          index,
          dropPosition,
          actionType
        );

        const updatedTemplate = {
          ...template.data,
          templateContent: {
            ...templateContent,
            pages: updatedPages,
          },
        };
        updateTemplate && updateTemplate(updatedTemplate);
        if (item?.tokenWarningFlag == 1) {
          $Message.warning(
            t(
              "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.PLEASE_CHECK_THE_AVAILABILITY_OF_TOKENS_IN_THIS_SECTION_WITH_THE_CATEGORY"
            )
          );
        }
      }
    },
    [template, content]
  );

  useEffect(() => {
    getTemplates && getTemplates({});
  }, []);

  useEffect(() => {
    setEditorControls(DEFAULT_MENU_DATA);
  }, [initTemplate?.saving]);

  useEffect(() => {
    if (
      (selectedTemplateId && templateId && templateId == selectedTemplateId) ||
      (templateId && selectedTemplateId == -1)
    ) {
      getTemplateDetails && getTemplateDetails({ templateId });
    }
    createGuides();
  }, [templateId]);

  /**
   * @description Handle onClick event when select a section
   * @param sectionName - Selected section template
   */
  const onSelectSection = (sectionName, pageIndex) => {
    try {
      if (section.name != sectionName) {
        const sectionData = getSectionData(sectionName);
        // update the redux state
        selectSection &&
          selectSection({
            pageIndex,
            ...sectionData,
          });
      }
      setEditorControls({
        ...editorControls,
        activeLeftPanel: LEFT_PANELS.SECTION_RULES,
      });
    } catch (error) {
      console.error("Select section issue : ", error);
    }
  };

  const handleOnChangeSectionProperties = (properties, name, rule) => {
    try {
      // update the redux state
      selectSection &&
        selectSection({
          ...section,
          properties,
        });
      const sectionToUpdate = getSectionByName(name, templateContent.pages);
      const sectionRule = getSectionRuleByRule(rule, sectionToUpdate);
      const updatedSectionRule = updateSectionRuleProperties(
        sectionRule,
        properties
      );

      const updatedTemplate = updateTemplateSectionRule(
        name,
        rule,
        updatedSectionRule,
        template.data
      );
      updateTemplate && updateTemplate(updatedTemplate);
    } catch (error) {
      console.error("handleOnChangeSectionProperties issue : ", error);
    }
  };

  const handleOnPageClick = () => {
    selectSection && selectSection(DEFAULT_SECTION_DATA);
    setEditorControls({
      ...editorControls,
      activeLeftPanel: LEFT_PANELS.PAGES_SECTIONS,
    });
  };

  /**
   * @description handle editor actions
   * @param action - action
   */
  const onHandleAction = (action) => {
    switch (action) {
      case EditorAction.TEMPLATE_RULE:
        openDrawer(DrawerType.TEMPLATE_RULE);
        break;
      case EditorAction.NEW_SECTION_RULE:
        openDrawer(DrawerType.SECTION_RULES);
        break;
      case EditorAction.CLOSE_RULES_DRAWER:
        manageDrawer && manageDrawer(DEFAULT_DRAWER_STATE);
        break;
      case EditorAction.PAGE_CLICK:
        handleOnPageClick();
        break;
      default:
        break;
    }
  };

  /**
   * @description Open the drawer by type
   * @param {DrawerType} type - Type of the drawer
   */
  const openDrawer = (type) => {
    // decide the suitable title for the drawer
    let title;
    switch (type) {
      case DrawerType.TEMPLATE_RULE:
        updateUrl(URLParams.DRAWER_TYPE, DrawerType.TEMPLATE_RULE);
        title = t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.TEMPLATE_RULE");
        break;
      case DrawerType.SECTION_RULES:
        updateUrl(URLParams.DRAWER_TYPE, DrawerType.SECTION_RULES);
        title = t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.SECTION_RULE_EDITOR");
        break;
      case DrawerType.NEW_REPEATING_ADAPTER:
        title = t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.REPEATING_ADAPTERS");
        break;
      default:
        "";
        break;
    }

    // change drawer state
    manageDrawer &&
      manageDrawer({
        type,
        title,
      });
  };

  /**
   * @description - Update the browser tab url according to given value
   * @param {number} profileId - Provision profile id if available
   */
  const updateUrl = (paramName, paramValue) => {
    urlParams.set(paramName, paramValue);
    navigate({ search: urlParams.toString() }, { replace: true });
  };

  const options = (pageIndex) => ({
    trim: true,
    replace: (domNode) => {
      if (domNode.name && domNode.name === "html") {
        return (
          <USCTemplate section="html">
            {domToReact(domNode.children, options)}
          </USCTemplate>
        );
      }
      if (
        domNode.name &&
        domNode.name === "div" &&
        domNode.attribs &&
        domNode.attribs.hasOwnProperty("sectionname")
      ) {
        const isHidden = domNode.attribs.hidden
          ? domNode.attribs.hidden
          : !Boolean(domNode.attribs.defaultsection);
        return (
          <USCSection
            id={domNode.attribs.id}
            repeatingAdapterId={domNode.attribs["repeatingadapterid"]}
            style={domNode.attribs.style}
            extraClassName={getUSCSectionStyleClass(
              domNode.attribs.sectionname,
              section,
              isHidden
            )}
            defaultSection={domNode.attribs.defaultsection}
            hidden={isHidden}
            rule={domNode.attribs.rule}
            name={domNode.attribs.sectionname}
            onClick={(sectionName) => onSelectSection(sectionName, pageIndex)}
            onChangeProperties={handleOnChangeSectionProperties}
            locked={domNode.attribs.locked}
          >
            {domToReact(domNode.children, {
              replace: (domNode) => {
                if (domNode.name && domNode.name === "params") {
                  const props = attributesToProps(domNode.attribs);
                  return (
                    <params {...props}>
                      {domToReact(domNode.children, options)}
                    </params>
                  );
                }
                if (
                  domNode.name &&
                  domNode.name === "p" &&
                  isEmpty(domNode.children)
                ) {
                  return <></>;
                }
              },
            })}
          </USCSection>
        );
      }

      if (domNode.name && domNode.name === "head") {
        return (
          <USCTemplate section="head">
            {domToReact(domNode.children, options)}
          </USCTemplate>
        );
      }

      if (domNode.name && domNode.name === "body") {
        return (
          <USCTemplate section="body">
            {domToReact(domNode.children, options)}
          </USCTemplate>
        );
      }
    },
  });

  const zoom = (state) => {
    let zoomCanvas = document.getElementById("pages-container");
    let newZoomLevel = zoomLevel;
    if (state == "in") {
      newZoomLevel = zoomLevel + 10;
    } else if (state == "out") {
      if (zoomLevel > 10) newZoomLevel = zoomLevel - 10;
    } else {
      newZoomLevel = 100;
    }
    zoomCanvas.setAttribute(
      "style",
      `transform: scale(${newZoomLevel}%); transform-origin: 0% 0% 0px;`
    );
    setZoomLevel(newZoomLevel);
  };

  const closePreview = () => {
    urlParams.has("preview") && urlParams.delete("preview");
    navigate({ search: urlParams.toString() }, { replace: true });
  };

  return (
    <>
      <NavigatePrompt />
      <div className="main-editor-layout d-flex flex-fill justify-content-center">
        <div className="left-panel">
          <div className="panel-content">
            <div className="tool-box">
              <$Tooltip placement="right" title="Pages & Sections">
                <div
                  className={`tool-item ${
                    editorControls.activeLeftPanel == LEFT_PANELS.PAGES_SECTIONS
                      ? "active"
                      : ""
                  }`}
                >
                  <$Button
                    icon={<FileAddOutlined />}
                    type={"link"}
                    size="large"
                    onClick={() =>
                      setEditorControls({
                        ...editorControls,
                        activeLeftPanel: LEFT_PANELS.PAGES_SECTIONS,
                      })
                    }
                    disabled={!template?.data}
                  />
                </div>
              </$Tooltip>
              <$Tooltip
                placement="right"
                title={t(
                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.TEMPLATE_RULES"
                )}
              >
                <div
                  className={`tool-item ${
                    editorControls.activeLeftPanel == LEFT_PANELS.TEMPLATE_RULES
                      ? "active"
                      : ""
                  }`}
                >
                  <$Button
                    icon={<FileDoneOutlined />}
                    type="link"
                    size="large"
                    onClick={() =>
                      setEditorControls({
                        ...editorControls,
                        activeLeftPanel: LEFT_PANELS.TEMPLATE_RULES,
                      })
                    }
                    disabled={!template?.data}
                  />
                </div>
              </$Tooltip>
              <$Tooltip
                placement="right"
                title={t(
                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.SECTION_SETTINGS"
                )}
              >
                <div
                  className={`tool-item ${
                    editorControls.activeLeftPanel == LEFT_PANELS.SECTION_RULES
                      ? "active"
                      : ""
                  }`}
                >
                  <$Button
                    icon={<AuditOutlined />}
                    type="link"
                    size="large"
                    onClick={() =>
                      setEditorControls({
                        ...editorControls,
                        activeLeftPanel: LEFT_PANELS.SECTION_RULES,
                      })
                    }
                    disabled={!template?.data}
                  />
                </div>
              </$Tooltip>
              <$Divider
                className="my-1"
                style={{ minWidth: "50%", width: "50%" }}
              />
              <$Tooltip
                placement="right"
                title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ZOOM_IN")}
              >
                <div className="tool-item">
                  <$Button
                    icon={<ZoomInOutlined />}
                    type="link"
                    size="large"
                    onClick={() => {
                      zoom("in");
                      setToolBarOffset(section?.id);
                    }}
                    disabled={!template?.data}
                  />
                </div>
              </$Tooltip>
              <$Tooltip
                placement="right"
                title={t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ZOOM_OUT")}
              >
                <div className="tool-item">
                  <$Button
                    icon={<ZoomOutOutlined />}
                    type="link"
                    size="large"
                    onClick={() => {
                      zoom("out");
                      setToolBarOffset(section?.id);
                    }}
                    disabled={!template?.data}
                  />
                </div>
              </$Tooltip>
              <$Divider
                className="my-1"
                style={{ minWidth: "50%", width: "50%" }}
              />
              <$Tooltip
                placement="right"
                title={t(
                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.VERSION_HISTORY"
                )}
              >
                <div
                  className={`tool-item ${
                    editorControls.activeLeftPanel ==
                    LEFT_PANELS.VERSION_HISTORY
                      ? "active"
                      : ""
                  }`}
                >
                  <$Button
                    type="link"
                    size="large"
                    data-testid="versionHistory"
                    onClick={() =>
                      setEditorControls({
                        ...editorControls,
                        activeLeftPanel: LEFT_PANELS.VERSION_HISTORY,
                      })
                    }
                    disabled={!template?.data}
                    style={{ fontSize: 12 }}
                  >
                    <BoxIcons
                      type={IconTypes.BOX_ICON}
                      name="version-history"
                    />
                  </$Button>
                </div>
              </$Tooltip>
            </div>
            <div className="property-content">
              <div className="d-flex flex-column">
                {editorControls.activeLeftPanel ==
                  LEFT_PANELS.PAGES_SECTIONS && (
                  <PagesAndSections
                    activePageIndex={editorControls.activePage}
                  />
                )}
                {editorControls.activeLeftPanel ==
                  LEFT_PANELS.TEMPLATE_RULES && (
                  <TemplateRules
                    onAddNewRule={() =>
                      onHandleAction(EditorAction.TEMPLATE_RULE)
                    }
                  />
                )}
                {editorControls.activeLeftPanel ==
                  LEFT_PANELS.SECTION_RULES && (
                  <SectionRules
                    onAddNewRule={() =>
                      onHandleAction(EditorAction.NEW_SECTION_RULE)
                    }
                  />
                )}
                {editorControls.activeLeftPanel ==
                  LEFT_PANELS.VERSION_HISTORY && <VersionHistory />}
              </div>
            </div>
          </div>
        </div>

        <div
          className="site-layout-background"
          onScroll={() => setToolBarOffset(section?.id)}
        >
          {zoomLevel !== 100 && (
            <div className="zoom-info">
              <span className="font-md text-muted">{zoomLevel}%</span>
              <$Divider type="vertical" />
              <$Button
                type="link"
                size="small"
                className="p-0"
                onClick={() => {
                  zoom("reset");
                  setToolBarOffset(section?.id);
                }}
                disabled={!template?.data}
              >
                {t("COMMON.RESET")}
              </$Button>
            </div>
          )}
          <div id="pages-container">
            {!template.data && (
              <div className="ruler-container horizontal py-5">
                <$Empty
                  image={$Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span>
                      {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.NO_CONTENT")}
                    </span>
                  }
                />
              </div>
            )}
            {templateContent.pages?.length > 0 && (
              <div className="ruler-container horizontal">
                <Ruler
                  {...rulerProps}
                  type="horizontal"
                  lineOffset={[0, -3]}
                  textAlign={"right"}
                />
              </div>
            )}
            {template.loading && (
              <div className="template-loading">
                <LottieAnimation lotti={LoadingPages} height={60} width={230} />
              </div>
            )}
            {templateContent.pages?.map((_page, index) => {
              return (
                <PageWrapper
                  key={index}
                  id={index}
                  pageProperties={pageProperties}
                  onChangeView={(id) => {
                    setEditorControls({
                      ...editorControls,
                      activePage: id,
                    });
                  }}
                  onClick={() => onHandleAction(EditorAction.PAGE_CLICK)}
                  onDrop={(item, dropPosition, actionType) => {
                    handleDrop(index, item, dropPosition, actionType);
                  }}
                >
                  <>
                    {templateContent.pages?.length > 0 && (
                      <div className="ruler-container vertical">
                        <Ruler
                          {...rulerProps}
                          type="vertical"
                          lineOffset={[-3, 0]}
                          textAlign={"right"}
                        />
                      </div>
                    )}

                    {content &&
                      content.querySelector(`[page=page_${index}]`) &&
                      HtmlParser(
                        content.querySelector(`[page=page_${index}]`).outerHTML,
                        options(index)
                      )}
                    <PageMargins index={index} />
                  </>
                </PageWrapper>
              );
            })}
          </div>
        </div>

        <div className="right-panel">
          <Parameters
            onAddRepeatingAdapter={() =>
              openDrawer(DrawerType.NEW_REPEATING_ADAPTER)
            }
          />
        </div>

        <$Drawer
          title={drawerInfo.title}
          placement={"right"}
          width={
            drawerInfo.type == DrawerType.NEW_REPEATING_ADAPTER
              ? "500px"
              : drawerInfo.size
          }
          onClose={() => onHandleAction(EditorAction.CLOSE_RULES_DRAWER)}
          open={drawerInfo.visible}
          destroyOnClose
          className={
            drawerInfo.type == DrawerType.RICH_TEXT_EDITOR
              ? "section-editor-deawer"
              : ""
          }
        >
          {[DrawerType.SECTION_RULES, DrawerType.TEMPLATE_RULE].includes(
            drawerInfo.type
          ) && (
            <RuleEditor
              drawerType={drawerInfo.type}
              onCancel={() => onHandleAction(EditorAction.CLOSE_RULES_DRAWER)}
            />
          )}

          {drawerInfo.type == DrawerType.NEW_REPEATING_ADAPTER && (
            <RepeatingAdapterChanger
              onClose={() => {
                onHandleAction(EditorAction.CLOSE_RULES_DRAWER);
              }}
            />
          )}
          {drawerInfo.type == DrawerType.RICH_TEXT_EDITOR && (
            <RichTextEditor
              onClose={() => onHandleAction(EditorAction.CLOSE_RULES_DRAWER)}
            />
          )}
          {drawerInfo.type == DrawerType.SMS_EDITOR && (
            <SMSEditor
              onClose={() => onHandleAction(EditorAction.CLOSE_RULES_DRAWER)}
            />
          )}
        </$Drawer>
        {content && urlParams.has("preview") && (
          <Preview
            defaultOptions={options}
            onClose={() => {
              closePreview();
            }}
          />
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  const { templateEditor, templatesHistory } = state;
  const { section, parameters, drawerInfo } = templateEditor;
  const { template, selectedTemplateId, initTemplate } = templatesHistory;
  return {
    section,
    parameters,
    template,
    drawerInfo,
    selectedTemplateId,
    initTemplate,
  };
};

const { section, templateRules } = Actions.templateEditor;
const { template, templates } = Actions.templatesHistory;

const mapDispatchToProps = {
  manageDrawer: templateRules.manageDrawer,
  selectSection: section.selectSection,
  getTemplateDetails: template.get,
  updateTemplate: template.update,
  getTemplates: templates.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(TemplateEditor);
